/**
 * Shows a banner on sites whose plan limits the number of team members, once they reach it.
 *
 * Owners and billing account managers are the only ones the server sends
 * org.billableMemberCount, and for them there are three states against the plan's limit:
 *  - under the limit: nothing.
 *  - at the limit: a nag with an upgrade link, dismissible (per user and site) until the site
 *    drops below the limit and reaches it again.
 *  - over the limit: the same, not dismissible.
 *
 * Upgrading is the remedy offered in every case.
 *
 * Everyone else gets billingAccount.status.overUserLimit but no count, and so a shorter
 * message that names no figures and points at the owner. Editors are told, viewers are not.
 *
 * A site can only be over the limit if it was already over when the limit arrived, since
 * members past the limit cannot be added. This is the warning while the site still works;
 * once its documents are actually held read-only, SiteReadOnlyBanner takes over.
 */

import { Banner, buildBannerMessage } from "app/client/components/Banner";
import { buildUpgradeMessage } from "app/client/components/DocumentUsage";
import { makeT } from "app/client/lib/localization";
import { AppModel } from "app/client/models/AppModel";
import { getUserOrgPrefObs, getUserOrgPrefsObs } from "app/client/models/UserPrefs";
import { canUpgradeOrg } from "app/common/roles";

import { Disposable, dom, makeTestId, Observable } from "grainjs";

const t = makeT("SiteUserLimitBanner");

const testId = makeTestId("test-site-user-limit-banner-");

export class SiteUserLimitBanner extends Disposable {
  private readonly _currentOrg = this._app.currentOrg;
  private readonly _limit = this._currentOrg?.billingAccount?.features?.maxUsersPerOrg;
  private readonly _count = this._currentOrg?.billableMemberCount;
  private readonly _status = this._currentOrg?.billingAccount?.status;

  // Whether the reader is someone who can lift the limit themselves, rather than someone who
  // has to ask. Billing managers can, whatever their access to the site. This is the same set
  // the server sends the count to.
  private readonly _canAct = this._app.isOwner() ||
    Boolean(this._currentOrg?.billingAccount?.isManager);

  // Dismissal is per user and org, so dismissing on one site does not silence another.
  private _dismissed: Observable<boolean | undefined> = this.autoDispose(
    getUserOrgPrefObs(
      this.autoDispose(getUserOrgPrefsObs(this._app)), "dismissedUserLimitBanner"));

  constructor(private _app: AppModel) {
    super();
  }

  public buildDom() {
    const limit = this._limit;
    const count = this._count;
    if (limit === undefined) { return null; }
    // Once the site is actually held read-only, SiteReadOnlyBanner says so and says what to do
    // about it. A warning that it may happen would only be in the way.
    if (this._currentOrg?.readOnlyReason) { return null; }
    if (!this._canAct || count === undefined) { return this._buildForOthersBanner(); }
    // A dismissal covers the site being at its limit now, not the next time it gets there.
    if (count < limit && this._dismissed.get()) { this._dismissed.set(undefined); }
    if (count > limit) { return this._buildOverLimitBanner(limit); }
    if (count === limit) { return this._buildAtLimitBanner(limit); }
    return null;
  }

  // Shown to people who cannot lift the limit themselves, in place of the banners above.
  // Editors are told, since they can pass it on. Viewers are not, as for subscription
  // banners: with nothing to act on and nothing to pass along, the message would be noise.
  private _buildForOthersBanner() {
    if (!this._status?.overUserLimit) { return null; }
    if (!this._app.isOwnerOrEditor()) { return null; }

    return dom.create(Banner, {
      content: buildBannerMessage(
        t("Your site has more team members than the free plan allows."),
        " ",
        // Points at the site owner for those who cannot upgrade, which is everyone here bar
        // an owner we were not told the count for.
        this._buildUpgradeMessage(),
        testId("text"),
      ),
      style: "warning",
      showCloseButton: false,
      showExpandButton: true,
    });
  }

  private _buildAtLimitBanner(limit: number) {
    return dom.domComputed((use) => {
      if (use(this._dismissed)) { return null; }

      return dom.create(Banner, {
        content: buildBannerMessage(
          t("Your site has reached the free plan limit of {{count}} team members.", { count: limit }),
          " ",
          this._buildUpgradeMessage(),
          testId("text"),
        ),
        contentSmall: buildBannerMessage(this._buildUpgradeMessage()),
        style: "warning",
        showCloseButton: true,
        showExpandButton: true,
        onClose: () => this._dismissed.set(true),
      });
    });
  }

  private _buildOverLimitBanner(limit: number) {
    return dom.create(Banner, {
      content: buildBannerMessage(
        t("Your site is over the free plan limit of {{count}} team members.", { count: limit }),
        " ",
        this._buildUpgradeMessage(),
        testId("text"),
      ),
      contentSmall: buildBannerMessage(this._buildUpgradeMessage()),
      style: "error",
      showCloseButton: false,
      showExpandButton: true,
    });
  }

  private _buildUpgradeMessage() {
    return buildUpgradeMessage(
      canUpgradeOrg(this._currentOrg),
      "long",
      () => this._app.showUpgradeModal(),
    );
  }
}
